import { useTusUploader } from '@hooks/useTusUploader';
import { messageService } from '@classes/messageService';

import { TFile, TFileBatch } from '../../types/file';
import { CRMAPIBase } from '../crmApiBase';
import { FileBatchResp, FileResp } from '../responseModels/file/fileResponse';
import { RequestResult } from '../responseModels/requestResult';
import { CRM_ID_REQUIRED_MESSAGE, isCrmIdMissing } from '../validateCrmId';

class FileGroup extends CRMAPIBase {
    /**
     * Строит результат с ошибкой при отсутствующем crm_id и уведомляет пользователя.
     * Используется методами, отправляющими запрос в обход CRMAPIBase.request().
     */
    private rejectMissingCrmId<T>(): RequestResult<T> {
        messageService.sendError(CRM_ID_REQUIRED_MESSAGE);
        const result = new RequestResult<T>();
        result.statusCode = -1;
        result.errorMessages = [CRM_ID_REQUIRED_MESSAGE];
        return result;
    }

    /**
     * Добавление файла
     * @param file Файл
     * @returns Результат запроса
     */
    async saveFile(file: TFile): Promise<RequestResult<FileResp>> {
        // return await sendRequest('/products', file, 'post');

        if (isCrmIdMissing(file)) {
            return this.rejectMissingCrmId<FileResp>();
        }

        let result = new RequestResult<FileResp>();
        try {
            const response = await this._httpClient.request<FileResp>({
                method: 'post',
                url: '/files',
                data: file,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            result.statusCode = response.status;
            if (response.status >= 200 && response.status <= 300) {
                result.data = response.data;
            } else {
                result = CRMAPIBase.parseErrors<FileResp>(response.status, response.data);
            }
        } catch (error) {
            result = CRMAPIBase.parseErrors<FileResp>(error.response.status, error.response.data);
        }
        return result;
    }
    /**
     * Удаление файла
     * @param file Объект из сущности-родителя
     * @returns Файл на замену удалённого
     */
    async removeFile(file: TFile): Promise<RequestResult<FileResp>> {
        const result = this.post<FileResp>(`/files/delete`, {
            crm_id: file.crm_id,
            picture: file.picture,
            entity_id: file.entity_id,
            model: file.model,
        });
        return result;
    }
    /**
     * Добавление массива файлов
     * @param files Файлы
     * @returns Результат запроса
     */
    async saveFileBatch(
        files: TFileBatch,
        onProgress?: (percentage: number, file: File) => void
    ): Promise<RequestResult<FileBatchResp>> {
        // crm_id для /batch/uploadImages необязателен на бэкенде (BatchActionRequest: 'crm_id' => ['nullable']) —
        // например, личные фото профиля пользователя не привязаны к конкретному CRM.
        const { uploadAll } = useTusUploader({
            uploadDir: files.model,
            crmID: String(files.crm_id),
            onProgress,
        });
        const uploadetPath = await uploadAll(files.pictures as File[]);
        if (!uploadetPath) {
            const result = new RequestResult<FileBatchResp>();
            result.statusCode = -1;
            result.errorMessages = ['Не удалось загрузить файл'];
            return result;
        }
        let result = new RequestResult<FileBatchResp>();
        const { pictures, defaultID, ...data } = {
            ...files,
            images: uploadetPath.map((item) => {
                return { ...item, default: false };
            }),
        };
        try {
            const response = await this._httpClient.request<FileBatchResp>({
                method: 'post',
                url: '/batch/uploadImages',
                data: data,
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            result.statusCode = response.status;
            if (response.status >= 200 && response.status <= 300) {
                result.data = response.data;
                if (defaultID) {
                    await this.setImageAsDefault(data.crm_id, defaultID);
                }
            } else {
                result = CRMAPIBase.parseErrors<FileBatchResp>(response.status, response.data);
            }
        } catch (error) {
            result = CRMAPIBase.parseErrors<FileBatchResp>(
                error.response.status,
                error.response.data
            );
        }
        return result;
    }
    async removeFileBatch(files: TFileBatch): Promise<RequestResult<FileBatchResp>> {
        const result = this.post<FileBatchResp>('/batch/deleteImages', {
            crm_id: files.crm_id,
            ids: files.ids,
        });
        return result;
    }

    async setImageAsDefault(crm_id, imageId): Promise<RequestResult<FileBatchResp>> {
        const result = this.post<FileBatchResp>(`/files/${imageId}/setImageAsDefault`, {
            crm_id: crm_id,
        });
        return result;
    }
}
export { FileGroup };
